
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ✅ Export All Fields to Excel (Auto-detects columns)
export const exportToExcel = (data, filename = "projects.xlsx") => {
  if (!data || data.length === 0) {
    // console.warn("No data to export to Excel");
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");
  XLSX.writeFile(workbook, filename);
};

// ✅ Improved PDF Export with Better Formatting
export const exportToPDF = (
  data,
  filename = "projects.pdf",
  reportType = "projects"
) => {
  const doc = new jsPDF("l", "pt", "a3"); // Use A3 for more space

  if (!data || data.length === 0) {
    // console.warn("No data to export to PDF");
    return;
  }

  // Define field mappings for different report types
  const projectFieldMapping = {
    visitedStatus: "Visited",
    projectName: "Project Name",
    area: "Area",
    projectAddress: "Address",
    propertyType: "Property Type",
    waterSupply: "Water Supply",
    propertyStatus: "Status",
    handOver: "Hand Over",
    noOfTower: "Towers",
    projectType: "Project Type",
    buildingFlooring: "Flooring",
    propertyOnFloor: "Floor No",
    projectContactName: "Contact Name",
    projectContactNumber: "Contact No",
    flatType: "Flat Type",
    flatFacing: "Facing",
    deductionPercentage: "Deduction %",
    ratePerSqft: "Rate/Sqft",
  };

  const customerFieldMapping = {
    leadDate: "Lead Date",
    leadFrom: "Lead From",
    customerName: "Customer Name",
    customerNumber: "Customer Number",
    requiredFlatType: "Required Flat Type",
    locationRequired: "Location Required",
    requiredResaleOrNewFlat: "Required Property  Type",
    budget: "Budget",
    followupDate: "Followup Date",
    clientFeedback: "Client Feedback",
    customFeedbackCategory: "Feedback Category",
    freshCall: "Fresh Call",
    addedBy: "Added By",
  };

  // Choose the appropriate field mapping
  const fieldMapping =
    reportType === "customers" ? customerFieldMapping : projectFieldMapping;

  const keys = Object.keys(data[0]);

  // Create headers using the mapping or formatted key names
  const formattedHeaders = keys.map(
    (key) =>
      fieldMapping[key] ||
      key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (c) => c.toUpperCase())
        .trim()
  );

  // Process data rows with type-specific formatting
  const rows = data.map((item) =>
    keys.map((key) => {
      let value = item[key];

      // Format date fields
      if (
        (key === "handOver" ) &&
        value
      ) {
        const date = new Date(value);
        value = date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }
      else if (
        (key === "leadDate" || key === "followupDate") &&
        value
      ) {
        const date = new Date(value);
        value = date.toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      }
      // Format boolean fields
      else if (key === "waterSupply" || key === "freshCall") {
        value = value ? "Yes" : "No";
      }
      // Format currency fields
      else if (key === "ratePerSqft" || key === "deductionPercentage") {
        value = value ? Number(value).toLocaleString("en-IN") : "-";
      }
      // Format budget field for customers
      else if (key === "budget" && value) {
        if (typeof value === "number") {
          if (value >= 10000000) {
            value = `${(value / 10000000).toFixed(1)}Cr`;
          } else if (value >= 100000) {
            value = `${(value / 100000).toFixed(1)}L`;
          } else {
            value = Number(value).toLocaleString("en-IN");
          }
        }
      }

      return value !== undefined && value !== null ? String(value) : "-";
    })
  );

  // Add title and date based on report type
  const reportTitle =
    reportType === "customers"
      ? "Customer Export Report"
      : "Project Export Report";
  const totalLabel =
    reportType === "customers" ? "Total Customers" : "Total Projects";

  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 51, 102);
  doc.text(reportTitle, 40, 40);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(102, 102, 102);
  const currentDate = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  doc.text(`Generated on: ${currentDate}`, 40, 60);
  doc.text(`${totalLabel}: ${data.length}`, 40, 75);

  // Calculate optimal column widths based on content
  const pageWidth = doc.internal.pageSize.width - 80; // 40px margin on each side
  const numColumns = keys.length;
  const baseColumnWidth = pageWidth / numColumns;

  // Define column width priorities based on report type
  const columnWidths = {};

  if (reportType === "customers") {
    // For customers, use fixed widths that fit better
    // const totalAvailableWidth = pageWidth;
    const customerColumnWidths = {
      leadDate: 70,
      leadFrom: 80,
      customerName: 120,
      customerNumber: 90,
      requiredFlatType: 70,
      locationRequired: 100,
      requiredResaleOrNewFlat: 80,
      budget: 70,
      followupDate: 70,
      clientFeedback: 150,
      customFeedbackCategory: 90,
      freshCall: 50,
      addedBy: 70,
    };

    keys.forEach((key, index) => {
      columnWidths[index] = {
        cellWidth: customerColumnWidths[key] || 60,
        minCellWidth: 40,
      };
    });
  } else {
    // Project-specific column widths (existing logic)
    keys.forEach((key, index) => {
      let width = baseColumnWidth;

      switch (key) {
        case "projectName":
        case "projectAddress":
          width = baseColumnWidth * 1.5;
          break;
        case "projectContactNumber":
          width = baseColumnWidth * 1.2;
          break;
        case "visitedStatus":
        case "waterSupply":
        case "noOfTower":
        case "propertyOnFloor":
        case "deductionPercentage":
          width = baseColumnWidth * 0.7;
          break;
        default:
          width = baseColumnWidth;
      }

      columnWidths[index] = { cellWidth: width };
    });
  }

  autoTable(doc, {
    head: [formattedHeaders],
    body: rows,
    startY: 90,
    margin: { top: 90, left: 20, right: 20, bottom: 40 }, // Reduced margins for customers
    theme: "striped",
    styles: {
      fontSize: reportType === "customers" ? 7 : 8, // Smaller font for customers
      cellPadding: { top: 3, right: 2, bottom: 3, left: 2 }, // Tighter padding for customers
      overflow: "linebreak",
      halign: "left",
      valign: "middle",
      lineColor: [220, 220, 220],
      lineWidth: 0.5,
    },
    columnStyles: columnWidths,
    headStyles: {
      fillColor: [0, 51, 102],
      textColor: [255, 255, 255],
      fontSize: reportType === "customers" ? 8 : 9, // Smaller header font for customers
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
      cellPadding: { top: 4, right: 2, bottom: 4, left: 2 }, // Tighter header padding
    },
    alternateRowStyles: {
      fillColor: [248, 249, 250],
    },
    rowStyles: {
      fillColor: [255, 255, 255],
    },
    tableWidth: "auto", // Let table auto-adjust width
    showHead: "everyPage",
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();

      // Footer with page number
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.setFont("helvetica", "normal");

      const footerY = doc.internal.pageSize.height - 20;
      const pageWidth = doc.internal.pageSize.width;

      // Left side - timestamp
      const footerDate = new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
      doc.text(`Exported: ${footerDate}`, 40, footerY);

      // Right side - page number
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        pageWidth - 100,
        footerY
      );

      // Center line
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.5);
      doc.line(40, footerY - 10, pageWidth - 40, footerY - 10);
    },
    didParseCell: (data) => {
      // Add styling based on content and report type
      if (data.section === "body") {
        if (reportType === "projects") {
          if (data.column.dataKey === keys.indexOf("propertyStatus")) {
            if (data.cell.raw === "Ready to Move") {
              data.cell.styles.textColor = [0, 128, 0]; // Green
              data.cell.styles.fontStyle = "bold";
            } else if (data.cell.raw === "Under Construction") {
              data.cell.styles.textColor = [255, 140, 0]; // Orange
            }
          }

          if (data.column.dataKey === keys.indexOf("propertyType")) {
            if (data.cell.raw === "Commercial") {
              data.cell.styles.textColor = [0, 0, 255]; // Blue
            } else if (data.cell.raw === "Residential") {
              data.cell.styles.textColor = [128, 0, 128]; // Purple
            }
          }
        } else if (reportType === "customers") {
          // Customer-specific styling
          if (data.column.dataKey === keys.indexOf("requiredResaleOrNewFlat")) {
            if (data.cell.raw === "New") {
              data.cell.styles.textColor = [0, 128, 0]; // Green
              data.cell.styles.fontStyle = "bold";
            } else if (data.cell.raw === "Resale") {
              data.cell.styles.textColor = [255, 140, 0]; // Orange
            }
          }

          if (data.column.dataKey === keys.indexOf("leadFrom")) {
            if (data.cell.raw === "Website") {
              data.cell.styles.textColor = [0, 0, 255]; // Blue
            } else if (data.cell.raw === "Social Media") {
              data.cell.styles.textColor = [128, 0, 128]; // Purple
            } else if (data.cell.raw === "Referral") {
              data.cell.styles.textColor = [0, 128, 0]; // Green
            }
          }
        }
      }
    },
  });

  doc.save(filename);
};

// ✅ Alternative compact PDF export for mobile viewing
export const exportToCompactPDF = (
  data,
  filename = "projects_compact.pdf",
  reportType = "projects"
) => {
  const doc = new jsPDF("p", "pt", "a4"); // Portrait A4

  if (!data || data.length === 0) {
    // console.warn("No data to export to PDF");
    return;
  }

  let essentialFields, headers, processData;

  if (reportType === "customers") {
    // Customer compact fields
    essentialFields = [
      "customerName",
      "customerNumber",
      "requiredFlatType",
      "locationRequired",
      "budget",
      "leadFrom",
      "clientFeedback",
    ];

    headers = [
      "Customer Name",
      "Phone",
      "Flat Type",
      "Location",
      "Budget",
      "Lead Source",
      "Feedback",
    ];

    processData = (items) =>
      items.map((item) => [
        item.customerName || "-",
        item.customerNumber || "-",
        item.requiredFlatType || "-",
        item.locationRequired || "-",
        item.budget
          ? typeof item.budget === "number"
            ? item.budget >= 10000000
              ? `${(item.budget / 10000000).toFixed(1)}Cr`
              : item.budget >= 100000
              ? `${(item.budget / 100000).toFixed(1)}L`
              : Number(item.budget).toLocaleString("en-IN")
            : item.budget
          : "-",
        item.leadFrom || "-",
        item.clientFeedback && item.clientFeedback.length > 30
          ? item.clientFeedback.substring(0, 30) + "..."
          : item.clientFeedback || "-",
      ]);
  } else {
    // Project compact fields (existing)
    essentialFields = [
      "projectName",
      "area",
      "propertyType",
      "propertyStatus",
      "flatType",
      "ratePerSqft",
      "projectContactNumber",
    ];

    headers = [
      "Project Name",
      "Area",
      "Type",
      "Status",
      "Configuration",
      "Rate/Sqft",
      "Contact",
    ];

    processData = (items) =>
      items.map((item) => [
        item.projectName || "-",
        item.area || "-",
        item.propertyType || "-",
        item.propertyStatus || "-",
        item.flatType || "-",
        item.ratePerSqft
          ? Number(item.ratePerSqft).toLocaleString("en-IN")
          : "-",
        item.projectContactNumber || "-",
      ]);
  }

  const compactData = data.map((item) => {
    const compactItem = {};
    essentialFields.forEach((field) => {
      compactItem[field] = item[field];
    });
    return compactItem;
  });

  const rows = processData(compactData);

  // Title
  const title =
    reportType === "customers"
      ? "Customers Summary Report"
      : "Projects Summary Report";
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 51, 102);
  doc.text(title, 40, 40);

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(102, 102, 102);
  doc.text(
    `Generated: ${new Date().toLocaleString("en-IN")} | Total: ${
      data.length
    } ${reportType}`,
    40,
    60
  );

  const columnWidths =
    reportType === "customers"
      ? {
          0: { cellWidth: 80 }, // Customer Name
          1: { cellWidth: 70 }, // Phone
          2: { cellWidth: 60 }, // Flat Type
          3: { cellWidth: 70 }, // Location
          4: { cellWidth: 50 }, // Budget
          5: { cellWidth: 60 }, // Lead Source
          6: { cellWidth: 100 }, // Feedback
        }
      : {
          0: { cellWidth: 80 }, // Project Name
          1: { cellWidth: 60 }, // Area
          2: { cellWidth: 60 }, // Type
          3: { cellWidth: 70 }, // Status
          4: { cellWidth: 50 }, // Configuration
          5: { cellWidth: 60 }, // Rate
          6: { cellWidth: 80 }, // Contact
        };

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 80,
    margin: { top: 80, left: 20, right: 20, bottom: 40 },
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 4,
      overflow: "linebreak",
      halign: "left",
      valign: "middle",
    },
    headStyles: {
      fillColor: [0, 51, 102],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: columnWidths,
    didDrawPage: (data) => {
      const pageCount = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(102, 102, 102);
      doc.text(
        `Page ${data.pageNumber} of ${pageCount}`,
        doc.internal.pageSize.width - 60,
        doc.internal.pageSize.height - 20
      );
    },
  });

  doc.save(filename);
};
